import {
  addDays,
  addMonths,
  diffDays,
  eachDay,
  monthOf,
  type HerdCounts,
  type TenantSettings,
} from '@dairy/shared';
import { compareEvents, groupEventsByAnimal, isAfter } from './events';
import { deriveLactations } from './lactation';
import { dailyTotals, enabledSessions } from './milk';
import { allocateExpense, reconcileMilkDay } from './money';
import { deriveReproStatus } from './repro';
import { deriveStage, isOnFarm } from './stage';
import {
  live,
  type DomainAnimal,
  type DomainCategory,
  type DomainEvent,
  type DomainExpense,
  type DomainMilkRecord,
  type DomainPayment,
  type DomainSale,
  type DomainUsage,
} from './types';

type KpiSettings = Pick<
  TenantSettings,
  'sessions' | 'calfMaxAgeMonths' | 'gestationDays' | 'dryPeriodDays' | 'unaccountedMilkThreshold'
>;

/** Milking days of each cow in [from, to] as a set of "animalId|date" keys. */
export function milkingCowDaySet(
  animals: readonly DomainAnimal[],
  events: readonly DomainEvent[],
  from: string,
  to: string,
): Set<string> {
  const out = new Set<string>();
  const byAnimal = groupEventsByAnimal(events);
  for (const a of live(animals)) {
    if (a.sex !== 'FEMALE') continue;
    for (const l of deriveLactations(a, byAnimal.get(a.id) ?? [])) {
      const start = l.calvingDate > from ? l.calvingDate : from;
      let end = l.endDate === null || l.endDate > to ? to : l.endDate;
      if (a.status !== 'ACTIVE' && a.exitDate && a.exitDate < end) end = a.exitDate;
      if (start > end) continue;
      for (let d = start; d <= end; d = addDays(d, 1)) out.add(`${a.id}|${d}`);
    }
  }
  return out;
}

/** D-11: number of (cow, day) pairs with stage MILKING in [from, to]. */
export function milkingCowDays(
  animals: readonly DomainAnimal[],
  events: readonly DomainEvent[],
  from: string,
  to: string,
): number {
  return milkingCowDaySet(animals, events, from, to).size;
}

export interface ProductionInput {
  records: readonly DomainMilkRecord[];
  usage: readonly DomainUsage[];
  sales: readonly DomainSale[];
  animals: readonly DomainAnimal[];
  events: readonly DomainEvent[];
  from: string;
  to: string;
  settings: Pick<KpiSettings, 'sessions'>;
}

export interface ProductionResult {
  producedMl: number;
  withheldMl: number;
  usageMl: number;
  saleableMl: number;
  soldMl: number;
  unaccountedMl: number;
  cowDaysWithRecords: number;
  litersPerCowPerDayMl: number | null;
  recordedCowSessions: number;
  expectedCowSessions: number;
  completeness: number | null;
  lateEntries: number;
  milkingCowDays: number;
}

/** D-8 + D-11 production figures for [from, to]. */
export function productionStats(input: ProductionInput): ProductionResult {
  const { from, to } = input;
  const records = live(input.records).filter((r) => r.date >= from && r.date <= to);
  let produced = 0;
  let withheld = 0;
  let late = 0;
  const cowDays = new Set<string>();
  for (const r of records) {
    produced += r.ml;
    if (r.withheld) withheld += r.ml;
    if (r.lateEntry) late += 1;
    cowDays.add(`${r.animalId}|${r.date}`);
  }
  let usage = 0;
  for (const u of live(input.usage)) if (u.date >= from && u.date <= to) usage += u.calfMl + u.homeMl + u.spoiledMl;
  let sold = 0;
  for (const s of live(input.sales)) if (s.type === 'MILK' && s.date >= from && s.date <= to) sold += s.milkMl ?? 0;
  const milkingDays = milkingCowDaySet(input.animals, input.events, from, to);
  const sessions = new Set(enabledSessions(input.settings).map((x) => x.session));
  const recorded = records.filter((r) => sessions.has(r.session) && milkingDays.has(`${r.animalId}|${r.date}`)).length;
  const expected = milkingDays.size * sessions.size;
  const saleable = produced - withheld - usage;
  return {
    producedMl: produced,
    withheldMl: withheld,
    usageMl: usage,
    saleableMl: saleable,
    soldMl: sold,
    unaccountedMl: saleable - sold,
    cowDaysWithRecords: cowDays.size,
    litersPerCowPerDayMl: cowDays.size ? Math.round(produced / cowDays.size) : null,
    recordedCowSessions: recorded,
    expectedCowSessions: expected,
    completeness: expected ? Math.min(1, recorded / expected) : null,
    lateEntries: late,
    milkingCowDays: milkingDays.size,
  };
}

export interface MoneyInput {
  sales: readonly DomainSale[];
  payments: readonly DomainPayment[];
  expenses: readonly DomainExpense[];
  categories: readonly DomainCategory[];
  from: string;
  to: string;
}

export interface MoneyResult {
  incomeByType: { MILK: number; ANIMAL: number; OTHER: number };
  incomeSantim: number;
  milkSoldMl: number;
  operatingCostSantim: number;
  feedCostSantim: number;
  investmentsSantim: number;
  operatingProfitSantim: number;
  cashInSantim: number;
  cashOutSantim: number;
  cashFlowSantim: number;
  /** Accrual operating cost per leaf category and per group (largest first). */
  byCategory: Array<{ categoryId: string; santim: number }>;
  byGroup: Array<{ categoryId: string; santim: number }>;
}

/**
 * D-10 / D-11 money figures. Accrual figures count whole months: every month touched by [from, to].
 * Cash figures use dates in [from, to].
 */
export function moneyStats(input: MoneyInput): MoneyResult {
  const { from, to } = input;
  const months = new Set<string>();
  for (let m = monthOf(from); m <= monthOf(to); m = monthOf(addMonths(`${m}-01`, 1))) months.add(m);
  const cats = new Map(input.categories.map((c) => [c.id, c]));
  const costTypeOf = (c: DomainCategory | undefined) =>
    c?.costType ?? (c?.parentId ? cats.get(c.parentId)?.costType : undefined) ?? 'OPERATING';

  const incomeByType = { MILK: 0, ANIMAL: 0, OTHER: 0 };
  let milkSold = 0;
  let cashIn = 0;
  for (const s of live(input.sales)) {
    if (s.date < from || s.date > to) continue;
    incomeByType[s.type] += s.amountSantim;
    if (s.type === 'MILK') milkSold += s.milkMl ?? 0;
    if (!s.buyerId) cashIn += s.amountSantim;
  }
  for (const p of live(input.payments)) if (p.date >= from && p.date <= to) cashIn += p.amountSantim;

  let operating = 0;
  let feed = 0;
  let investments = 0;
  let cashOut = 0;
  const byCat = new Map<string, number>();
  const byGroup = new Map<string, number>();
  for (const e of live(input.expenses)) {
    const cat = cats.get(e.categoryId);
    const ct = costTypeOf(cat);
    if (e.date >= from && e.date <= to) {
      cashOut += e.amountSantim;
      if (ct === 'CAPITAL') investments += e.amountSantim;
    }
    if (ct !== 'OPERATING') continue;
    for (const a of allocateExpense(e)) {
      if (!months.has(a.month)) continue;
      operating += a.santim;
      if (cat?.isFeed) feed += a.santim;
      byCat.set(e.categoryId, (byCat.get(e.categoryId) ?? 0) + a.santim);
      const g = cat?.parentId ?? e.categoryId;
      byGroup.set(g, (byGroup.get(g) ?? 0) + a.santim);
    }
  }
  const income = incomeByType.MILK + incomeByType.ANIMAL + incomeByType.OTHER;
  const sorted = (m: Map<string, number>) =>
    [...m.entries()]
      .map(([categoryId, santim]) => ({ categoryId, santim }))
      .sort((a, b) => b.santim - a.santim || (a.categoryId < b.categoryId ? -1 : 1));
  return {
    incomeByType,
    incomeSantim: income,
    milkSoldMl: milkSold,
    operatingCostSantim: operating,
    feedCostSantim: feed,
    investmentsSantim: investments,
    operatingProfitSantim: income - operating,
    cashInSantim: cashIn,
    cashOutSantim: cashOut,
    cashFlowSantim: cashIn - cashOut,
    byCategory: sorted(byCat),
    byGroup: sorted(byGroup),
  };
}

export interface UnitEconomicsResult {
  costPerLiterSantim: number | null;
  feedCostPerLiterSantim: number | null;
  averageMilkPriceSantim: number | null;
  marginPerLiterSantim: number | null;
  profitPerMilkingCowSantim: number | null;
}

/** D-11 unit economics. Ratios are rounded to whole santim. */
export function unitEconomics(
  production: Pick<ProductionResult, 'producedMl'>,
  money: Pick<MoneyResult, 'operatingCostSantim' | 'feedCostSantim' | 'incomeByType' | 'milkSoldMl' | 'operatingProfitSantim'>,
  milkingCowDayCount: number,
  days: number,
): UnitEconomicsResult {
  const perLiter = (santim: number) =>
    production.producedMl > 0 ? Math.round((santim * 1000) / production.producedMl) : null;
  const cost = perLiter(money.operatingCostSantim);
  const price = money.milkSoldMl > 0 ? Math.round((money.incomeByType.MILK * 1000) / money.milkSoldMl) : null;
  const avgCows = days > 0 ? milkingCowDayCount / days : 0;
  return {
    costPerLiterSantim: cost,
    feedCostPerLiterSantim: perLiter(money.feedCostSantim),
    averageMilkPriceSantim: price,
    marginPerLiterSantim: price !== null && cost !== null ? price - cost : null,
    profitPerMilkingCowSantim: avgCows > 0 ? Math.round(money.operatingProfitSantim / avgCows) : null,
  };
}

/** AR-5 / D-11 herd by stage on `date` (animals on the farm that day). */
export function herdStats(
  animals: readonly DomainAnimal[],
  events: readonly DomainEvent[],
  date: string,
  s: Pick<KpiSettings, 'calfMaxAgeMonths' | 'gestationDays' | 'dryPeriodDays'>,
): HerdCounts {
  const byAnimal = groupEventsByAnimal(events);
  const c: HerdCounts = { milking: 0, dry: 0, heifers: 0, femaleCalves: 0, maleCalves: 0, bulls: 0, pregnant: 0, total: 0 };
  for (const a of live(animals)) {
    if (!isOnFarm(a, date)) continue;
    const evs = byAnimal.get(a.id) ?? [];
    const stage = deriveStage(a, evs, date, s);
    c.total += 1;
    if (stage === 'MILKING') c.milking += 1;
    else if (stage === 'DRY') c.dry += 1;
    else if (stage === 'HEIFER') c.heifers += 1;
    else if (stage === 'BULL') c.bulls += 1;
    else if (stage === 'CALF') {
      if (a.sex === 'FEMALE') c.femaleCalves += 1;
      else c.maleCalves += 1;
    }
    if (a.sex === 'FEMALE' && deriveReproStatus(a, evs, date, s)?.status === 'PREGNANT') c.pregnant += 1;
  }
  return c;
}

export interface BreedingKpiResult {
  calvingIntervalDays: number | null;
  calvingIntervalCount: number;
  daysOpen: number | null;
  daysOpenCount: number;
  servicesPerConception: number | null;
  conceptionCount: number;
}

const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

/** D-11 breeding KPIs over the 12 months ending `asOf`. */
export function breedingKpis(
  animals: readonly DomainAnimal[],
  events: readonly DomainEvent[],
  asOf: string,
): BreedingKpiResult {
  const since = addMonths(asOf, -12);
  const byAnimal = groupEventsByAnimal(events);
  const intervals: number[] = [];
  const daysOpen: number[] = [];
  let services = 0;
  let conceptions = 0;
  for (const a of live(animals)) {
    if (a.sex !== 'FEMALE') continue;
    const evs = (byAnimal.get(a.id) ?? []).filter((e) => e.date <= asOf);
    const calvings = evs.filter((e) => e.type === 'CALVING');
    // Calving interval: consecutive calvings, the later one in the last 12 months.
    for (let i = 1; i < calvings.length; i++) {
      const later = calvings[i]!;
      if (later.date > since && !later.isOpening) intervals.push(diffDays(later.date, calvings[i - 1]!.date));
    }
    // Pregnancies confirmed in the last 12 months (one per conceiving service).
    const seen = new Set<string>();
    for (const check of evs) {
      if (check.type !== 'PREG_CHECK' || (check.data as { result?: string }).result !== 'PREGNANT') continue;
      if (check.date <= since) continue;
      const before = evs.filter((e) => compareEvents(e, check) < 0);
      const service = [...before].reverse().find((e) => e.type === 'SERVICE');
      if (!service || seen.has(service.id)) continue;
      // The check must belong to the service's cycle (no calving between them).
      if (before.some((e) => e.type === 'CALVING' && isAfter(e, service))) continue;
      seen.add(service.id);
      const calving = [...before].reverse().find((e) => e.type === 'CALVING' && isAfter(service, e));
      if (calving) daysOpen.push(diffDays(service.date, calving.date));
      const cycleServices = evs.filter(
        (e) => e.type === 'SERVICE' && compareEvents(e, service) <= 0 && (!calving || isAfter(e, calving)),
      );
      const hasOpening = cycleServices.some((e) => e.isOpening) || !!check.isOpening;
      if (!hasOpening) {
        services += cycleServices.length;
        conceptions += 1;
      }
    }
  }
  const ci = mean(intervals);
  const dO = mean(daysOpen);
  return {
    calvingIntervalDays: ci === null ? null : Math.round(ci),
    calvingIntervalCount: intervals.length,
    daysOpen: dO === null ? null : Math.round(dO),
    daysOpenCount: daysOpen.length,
    servicesPerConception: conceptions ? Math.round((services / conceptions) * 100) / 100 : null,
    conceptionCount: conceptions,
  };
}

export interface CowRank {
  animalId: string;
  tag: string;
  name: string | null;
  litersPerDayMl: number;
  days: number;
}

/** RP-2: top 3 and bottom 3 cows by liters per day (days with at least one record). */
export function cowRanking(
  records: readonly DomainMilkRecord[],
  animals: readonly DomainAnimal[],
  _events: readonly DomainEvent[],
  from: string,
  to: string,
): { top: CowRank[]; bottom: CowRank[]; all: CowRank[] } {
  const byCow = new Map<string, { ml: number; days: Set<string> }>();
  for (const r of live(records)) {
    if (r.date < from || r.date > to) continue;
    const v = byCow.get(r.animalId) ?? { ml: 0, days: new Set<string>() };
    v.ml += r.ml;
    v.days.add(r.date);
    byCow.set(r.animalId, v);
  }
  const animalsById = new Map(animals.map((a) => [a.id, a]));
  const all: CowRank[] = [...byCow.entries()]
    .map(([animalId, v]) => {
      const a = animalsById.get(animalId);
      return {
        animalId,
        tag: a?.tag ?? '?',
        name: a?.name ?? null,
        litersPerDayMl: Math.round(v.ml / v.days.size),
        days: v.days.size,
      };
    })
    .sort((a, b) => b.litersPerDayMl - a.litersPerDayMl || a.tag.localeCompare(b.tag));
  return { top: all.slice(0, 3), bottom: [...all].reverse().slice(0, 3), all };
}

export interface MonthHerdEvents {
  births: number;
  exits: { SOLD: number; DEAD: number; CULLED: number };
  calvings: number;
  services: number;
  pregnanciesConfirmed: number;
}

/** RP-2 herd and breeding counts for events dated in [from, to] (opening history excluded). */
export function herdEventCounts(
  animals: readonly DomainAnimal[],
  events: readonly DomainEvent[],
  from: string,
  to: string,
): MonthHerdEvents {
  const out: MonthHerdEvents = { births: 0, exits: { SOLD: 0, DEAD: 0, CULLED: 0 }, calvings: 0, services: 0, pregnanciesConfirmed: 0 };
  for (const a of live(animals)) {
    if (a.source === 'BORN_ON_FARM' && a.birthDate >= from && a.birthDate <= to) out.births += 1;
    if (a.status !== 'ACTIVE' && a.exitDate && a.exitDate >= from && a.exitDate <= to) out.exits[a.status] += 1;
  }
  for (const e of live(events)) {
    if (e.isOpening || e.date < from || e.date > to) continue;
    if (e.type === 'CALVING') out.calvings += 1;
    if (e.type === 'SERVICE') out.services += 1;
    if (e.type === 'PREG_CHECK' && (e.data as { result?: string }).result === 'PREGNANT') out.pregnanciesConfirmed += 1;
  }
  return out;
}

export interface AdoptionResult {
  from: string;
  to: string;
  expectedCowSessions: number;
  recordedCowSessions: number;
  completeness: number | null;
  lateEntries: number;
  daysWithUnaccountedMilk: number;
  expensesRecorded: number;
  salesRecorded: number;
}

/** RP-6: adoption and data quality for the 30 days ending `asOf`. */
export function adoptionStats(
  records: readonly DomainMilkRecord[],
  animals: readonly DomainAnimal[],
  events: readonly DomainEvent[],
  usage: readonly DomainUsage[],
  sales: readonly DomainSale[],
  expenses: readonly DomainExpense[],
  s: Pick<KpiSettings, 'sessions' | 'unaccountedMilkThreshold'>,
  asOf: string,
): AdoptionResult {
  const from = addDays(asOf, -29);
  const p = productionStats({ records, usage, sales, animals, events, from, to: asOf, settings: s });
  let unaccountedDays = 0;
  for (const d of eachDay(from, asOf)) {
    const t = dailyTotals(records, usage, d);
    if (t.producedMl === 0) continue;
    const sold = live(sales)
      .filter((x) => x.type === 'MILK' && x.date === d)
      .reduce((acc, x) => acc + (x.milkMl ?? 0), 0);
    if (reconcileMilkDay(t.saleableMl, sold, s.unaccountedMilkThreshold).flag !== 'OK') unaccountedDays += 1;
  }
  return {
    from,
    to: asOf,
    expectedCowSessions: p.expectedCowSessions,
    recordedCowSessions: p.recordedCowSessions,
    completeness: p.completeness,
    lateEntries: p.lateEntries,
    daysWithUnaccountedMilk: unaccountedDays,
    expensesRecorded: live(expenses).filter((e) => e.date >= from && e.date <= asOf).length,
    salesRecorded: live(sales).filter((x) => x.date >= from && x.date <= asOf).length,
  };
}
